import type React from 'react';

import { Avatar } from '@change/ds-core-ui';

import { Translate, TranslateHtml } from '@change-corgi/core/react/i18n';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { PetitionActivityLink } from 'src/app/pages/starterDashboardV2/shared/components';
import { InfoTooltip } from 'src/app/shared/components/tooltips';
import { useProcessedImageUrl } from 'src/app/shared/hooks/image';

import { ExportButton } from '../../../../../shared/ExportButton';
import { useExportButton } from '../../../../../shared/ExportButton/hooks/useExportButton';
import { ExportNotifications } from '../../../../../StarterDashboardComments/components/ExportNotifications';

import { type RecentSupporter, useRecentSupportersList } from './hooks/useRecentSupportersList';

const SupporterItem: React.FC<{ supporter: RecentSupporter }> = ({ supporter }) => {
	const processedPhoto = useProcessedImageUrl(supporter.photo);

	return (
		<li
			className="flex w-full items-center gap-2"
			key={supporter.uniqueKey}
			data-testid={`recent-supporter-item-new-${supporter.uniqueKey}`}
		>
			{/* Avatar */}
			<Avatar
				alt="avatar"
				size="small"
				src={processedPhoto}
				data-testid={`recent-supporter-avatar-new-${supporter.uniqueKey}`}
			/>

			{/* Name and timestamp */}
			<p
				className="text-content-strong-color text-size-body font-sans"
				data-testid={`recent-supporter-text-new-${supporter.uniqueKey}`}
			>
				<TranslateHtml
					value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.name_with_sign_time"
					replacements={{
						name: supporter.displayName,
						relative_time: supporter.relativeTimestamp,
					}}
				/>
			</p>
		</li>
	);
};

export const RecentSupportersList: React.FC = () => {
	const { data, actions } = useRecentSupportersList();
	const { data: exportData, actions: exportActions } = useExportButton('signatures');
	const {
		data: {
			petition: { slug },
			featureConfigs: { starterExperiencePetitionActivity: starterExperiencePetitionActivityFCM },
		},
	} = useStarterDashboardV2PageData();

	return (
		<div className="relative flex w-full flex-col">
			<div
				className="overflow-hidden bg-white"
				data-qa="starter-dashboard-recent-supporters-card-new"
				data-testid="starter-dashboard-recent-supporters-card-new"
				aria-label="export-recent-supporters-card-new"
				aria-describedby="export-recent-supporters-card-new"
			>
				{/* Header Section */}
				<div className="flex items-center gap-2 pb-px" data-testid="recent-supporters-header-new">
					<div className="flex gap-1">
						<h3
							className="text-size-heading-small text-typography-strong font-sans"
							id="recent-supporters-title-new"
							data-testid="recent-supporters-title-new"
						>
							{starterExperiencePetitionActivityFCM ? (
								<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.title_signers" />
							) : (
								<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.title" />
							)}
						</h3>
						<InfoTooltip aria-describedby="recent-supporters-title-new" data-testid="recent-supporters-tooltip-new">
							<Translate value="corgi.petition.starter_dashboard.engage_your_supporters.recent_supporters.tip" />
						</InfoTooltip>
					</div>

					<div className="ml-auto justify-self-end" data-testid="recent-supporters-export-container-new">
						{starterExperiencePetitionActivityFCM ? (
							<PetitionActivityLink slug={slug} linkType="signatures" />
						) : (
							<ExportButton
								exportType="SIGNATURES"
								data-testid="export-recent-supporters-button-new"
								data-qa="export-recent-supporters-button-new"
								aria-label="export-recent-supporters-button-new"
								aria-describedby="export-recent-supporters-button-new"
								data={exportData}
								actions={exportActions}
							/>
						)}
					</div>
				</div>

				{/* Supporters List */}
				<div data-testid="recent-supporters-body-new">
					<ul
						className="flex max-h-[357px] flex-1 flex-col gap-4 overflow-auto p-0 pt-5 backdrop-blur-sm md:p-0 md:pt-6"
						sx={{
							backdropFilter: 'blur(8px)',
							WebkitBackdropFilter: 'blur(8px)',
							maskImage: 'linear-gradient(to top, transparent 0%, black 22%)',
							WebkitMaskImage: 'linear-gradient(to top, transparent 0%, black 22%)',
						}}
						onScroll={actions.onScrollSupporters}
						data-testid="recent-supporters-list-new"
					>
						{data.recentSupporters.map((supporter) => (
							<SupporterItem key={supporter.uniqueKey} supporter={supporter} />
						))}
						<li className="-mt-4 min-h-8 md:min-h-12" />
					</ul>
				</div>
			</div>
			<ExportNotifications data={exportData} />
		</div>
	);
};
